import {
  ConfirmPasswordRequirementsMessage,
  EMAIL_PATTERN,
  EmailRequirementsMessage,
  PasswordRequirementsMessage,
  RequirementsMessage,
} from 'src/components/Forms/constants';
import { NumberType } from 'src/components/Forms/CustomFormulaForm';
import {
  CustomFormulaItemType,
  MetricFormatType,
  UsageOption,
} from 'src/utils/gql/types';
import * as yup from 'yup';

const password = yup
  .string()
  .required(PasswordRequirementsMessage.Required)
  .matches(/[A-Z]/, PasswordRequirementsMessage.MustContainUppercaseLetter)
  .matches(/[0-9]/, PasswordRequirementsMessage.MustContainNumber)
  .matches(
    /[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/,
    PasswordRequirementsMessage.MustContainSymbol,
  )
  .matches(/^(?=.{8,})/, PasswordRequirementsMessage.MinLength);

// Per createAccountFormSchema e setNewPasswordFormSchema (usa 'password')
const repeatPasswordForPassword = yup
  .string()
  .oneOf(
    [yup.ref('password')],
    ConfirmPasswordRequirementsMessage.PasswordsMustMatch,
  )
  .required();

// Per changePasswordFormSchema (usa 'newPassword')
const repeatPasswordForNewPassword = yup
  .string()
  .oneOf(
    [yup.ref('newPassword')],
    ConfirmPasswordRequirementsMessage.PasswordsMustMatch,
  )
  .required();

const email = yup
  .string()
  .required(EmailRequirementsMessage.Required)
  .matches(EMAIL_PATTERN, EmailRequirementsMessage.ValidEmail);

export const createAccountFormSchema = yup
  .object()
  .shape({
    email,
    password,
    repeatPassword: repeatPasswordForPassword, // ✅ Usa 'password'
    firstName: yup.string().required(RequirementsMessage.FirstName),
    lastName: yup.string().required(RequirementsMessage.LastName),
    policy: yup.boolean().oneOf([true]).required(),
  })
  .required();

export const companyFormSchema = yup
  .object()
  .shape({
    name: yup.string().required(RequirementsMessage.CompanyName),
    industry: yup.string().required(RequirementsMessage.Industry),
    companyDescription: yup
      .string()
      .required(RequirementsMessage.CompanyDescription),
    usageOptions: yup.string().required().oneOf(Object.values(UsageOption)),
  })
  .required();

export const signInSchema = yup
  .object()
  .shape({
    email,
    password: yup.string().required(PasswordRequirementsMessage.Required),
  })
  .required();

export const resetPasswordFormSchema = yup
  .object()
  .shape({
    email,
  })
  .required();

export const setNewPasswordFormSchema = yup
  .object()
  .shape({
    password,
    repeatPassword: repeatPasswordForPassword, // ✅ Usa 'password'
  })
  .required();

export const changePasswordFormSchema = yup
  .object()
  .shape({
    email: yup.string().required(),
    currentPassword: yup.string().required('Current password is required'),
    newPassword: password,
    repeatPassword: repeatPasswordForNewPassword, // ✅ Usa 'newPassword'
  })
  .required();

const createFilterSchema = () =>
  yup.array().of(
    yup.lazy((value) => {
      if (value && value.filtersGroup) {
        return FiltersGroupSchema;
      }
      return MetricFilterItemSchema;
    }),
  );

const MetricFilterItemSchema = yup.object().shape({
  key: yup.string().required(),
  operator: yup.string().required(),
  values: yup.array().of(yup.string().required()).required().min(1).required(),
});

const FiltersGroupSchema = yup.object().shape({
  filtersGroup: yup.array().of(MetricFilterItemSchema).required(),
});

const FormatSchema = yup.object().shape({
  type: yup.string().oneOf(Object.values(MetricFormatType)).required(),
  numberType: yup.string().oneOf(Object.values(NumberType)),
});

const FormulaItemSchema = yup.object().shape({
  label: yup.string().required(),
  value: yup.string().required(),
  type: yup.string().oneOf(Object.values(CustomFormulaItemType)).required(),
  width: yup.number().required(),
  id: yup.string().required(),
  filters: createFilterSchema(),
});

export const customFormulaFormSchema = yup.object().shape({
  name: yup.string().required(RequirementsMessage.FormulaName),
  format: FormatSchema.required(),
  formulaItems: yup.array().of(FormulaItemSchema).required().min(1).required(),
});
